// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Meta } from '../types';
import { glossaryTerm } from '../glossary';
import { formatDate, formatNumber } from '../utils/format';

/** About modal: what this is, where the data comes from, and what it cannot tell you. */
export function createAbout(meta: Meta): { open: () => void; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'About Baby Names');

  const nswBoy = meta.latest['NSW-boy'];
  const nswGirl = meta.latest['NSW-girl'];

  modal.innerHTML = `
    <button class="modal-close" aria-label="Close">✕</button>
    <h2>About Baby Names</h2>
    <p>
      This is ${formatNumber(meta.totals.distinctNames)} distinct names drawn from
      ${formatNumber(meta.totals.records)} yearly ${glossaryTerm('rank', 'rankings')} — every name in the
      ${glossaryTerm('top100', 'top 100')} boys' and girls' lists that New South Wales (from 1952) and Queensland
      (from 1960) have published each year, up to ${meta.latestYear}. In ${meta.latestYear} NSW's most-registered
      names were <strong>${nswBoy ? nswBoy.no1 : '—'}</strong> for boys and
      <strong>${nswGirl ? nswGirl.no1 : '—'}</strong> for girls.
    </p>
    <p>
      It exists to show something a single year's top-10 list hides: naming has fragmented. In the 1950s a handful of
      names covered most babies; today the #1 name is given to a small fraction of what the #1 name once was. Type a
      name into the search box to trace its own rise and fall, or open the ${glossaryTerm('concentration', 'Diversity')}
      view to watch the whole shift.
    </p>

    <h3>Where the data comes from</h3>
    <ul>
      <li>
        <strong>NSW Registry of Births, Deaths &amp; Marriages</strong> — Popular Baby Names, 1952–2025: rank, name,
        number of babies, sex and year.
      </li>
      <li>
        <strong>Queensland Registry of Births, Deaths &amp; Marriages</strong> — Top 100 Baby Names, 1960–2025, via
        the Queensland open-data portal.
      </li>
    </ul>

    <h3>How often it updates</h3>
    <p>
      An automated pipeline rebuilds the site once a year, matching the registries' annual release cycle. Data last
      built ${formatDate(meta.generated)} (latest year: ${meta.latestYear}).
    </p>

    <h3>Important limits — please read</h3>
    <ul>
      <li>
        <strong>Two states, not the whole country.</strong> ${glossaryTerm('coverage', 'Only NSW and Queensland')}
        publish long, open, count-level histories. Victoria, WA, SA, Tasmania, the ACT and the NT are not included.
        Together NSW and QLD are close to half of all Australian births, and the fashions they show are national — but a
        name can be more or less popular elsewhere.
      </li>
      <li>
        <strong>Only the top 100 each year.</strong> ${glossaryTerm('top100', 'The registries release just the 100 most popular names')}
        per sex per year. A name that "disappears" from a chart has left the top 100 — it does not mean no baby was
        given it. Rare names are invisible here.
      </li>
      <li>
        <strong>Spellings are separate.</strong> ${glossaryTerm('count', 'Mia and Mya, or Aiden and Ayden, are counted as different names')}
        — which is part of why no single name dominates the way it used to.
      </li>
      <li>
        <strong>Registration year, not birth year.</strong> ${glossaryTerm('registration_year', 'Names are grouped by the year the birth was registered')},
        which can lag the birth by a few months.
      </li>
    </ul>

    <h3>Licence</h3>
    <p>
      NSW data is published under CC BY 4.0 and Queensland data under CC BY 3.0 AU. This site is an independent project
      and is not affiliated with either registry.
    </p>
  `;

  document.body.append(overlay, modal);
  const close = () => {
    overlay.classList.remove('open');
    modal.classList.remove('open');
  };
  const open = () => {
    overlay.classList.add('open');
    modal.classList.add('open');
    (modal.querySelector('.modal-close') as HTMLElement)?.focus();
  };
  overlay.addEventListener('click', close);
  modal.querySelector('.modal-close')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) close();
  });
  return { open, close };
}
